import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import {
  countPendingCases,
  coverageCasePath,
  listCoverageCases,
  syncCoverageCasesFromPrescriptions,
} from "@/core/insurance/coverage-case-store";
import { listInsurers } from "@/core/insurance/insurer-store";
import { COVERAGE_CASE_STATUS_LABELS } from "@/core/insurance/types";
import { listPatientsByInsurer } from "@/core/patients/patient-store";
import { routes } from "@/router/all_routes";

export function InsuranceHomePage() {
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    syncCoverageCasesFromPrescriptions();
    const refresh = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:coverage-cases", refresh);
    window.addEventListener("hospitol:insurers", refresh);
    window.addEventListener("hospitol:clinical-prescriptions", refresh);
    return () => {
      window.removeEventListener("hospitol:coverage-cases", refresh);
      window.removeEventListener("hospitol:insurers", refresh);
      window.removeEventListener("hospitol:clinical-prescriptions", refresh);
    };
  }, []);

  const stats = useMemo(() => {
    void refreshKey;
    const insurers = listInsurers();
    const cases = listCoverageCases();
    const pending = countPendingCases();
    const members = insurers.reduce(
      (sum, ins) => sum + listPatientsByInsurer(ins.id).length,
      0,
    );
    return { insurers: insurers.length, cases: cases.length, pending, members };
  }, [refreshKey]);

  const recentCases = useMemo(() => {
    void refreshKey;
    return listCoverageCases().slice(0, 5);
  }, [refreshKey]);

  return (
    <div>
      <PageHeader
        title="Assurance médicale"
        description="Assureurs partenaires, dossiers de prise en charge et liaison avec le hub central."
        actions={
          <div className="ph-page-header-actions">
            <a
              href={import.meta.env.VITE_INSURANCE_HUB_URL ?? "http://localhost:3119"}
              target="_blank"
              rel="noreferrer"
              className="btn btn-outline-primary btn-sm"
            >
              Hub assureur
            </a>
          </div>
        }
      />

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="ph-invoice-sheet h-100 text-center">
            <div className="display-6 fw-bold">{stats.insurers}</div>
            <div className="small text-muted">Assureurs actifs</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="ph-invoice-sheet h-100 text-center">
            <div className="display-6 fw-bold">{stats.members}</div>
            <div className="small text-muted">Adhérents liés</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="ph-invoice-sheet h-100 text-center">
            <div className="display-6 fw-bold">{stats.cases}</div>
            <div className="small text-muted">Dossiers prise en charge</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="ph-invoice-sheet h-100 text-center">
            <div className="display-6 fw-bold text-warning">{stats.pending}</div>
            <div className="small text-muted">En attente validation</div>
          </div>
        </div>
      </div>

      <div className="row g-3 ph-split-workspace">
        <div className="col-lg-6">
          <div className="ph-invoice-sheet h-100">
            <h3 className="h6 fw-semibold mb-3">Accès rapide</h3>
            <div className="d-grid gap-2">
              <Link to={routes.insuranceInsurers} className="btn btn-outline-secondary btn-sm text-start">
                Compagnies d&apos;assurance
              </Link>
              <Link to={routes.insuranceCases} className="btn btn-outline-secondary btn-sm text-start">
                Dossiers de prise en charge
              </Link>
              <Link to={routes.insurancePartnerships} className="btn btn-outline-primary btn-sm text-start">
                Partenariats hub assureur
              </Link>
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="ph-invoice-sheet h-100">
            <h3 className="h6 fw-semibold mb-3">Dossiers récents</h3>
            {recentCases.length === 0 ? (
              <p className="small text-muted mb-0">Aucun dossier — liez des patients à un assureur.</p>
            ) : (
              <ul className="list-unstyled mb-0 small">
                {recentCases.map((c) => (
                  <li key={c.id} className="mb-2">
                    <Link to={coverageCasePath(c.id)}>{c.label}</Link>
                    <span className="badge bg-secondary ms-2">
                      {COVERAGE_CASE_STATUS_LABELS[c.status]}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
